import React from 'react';
import { GraduationCap, School, ChevronRight, User, BookOpen } from 'lucide-react';
import AuthLayout from '../ui/AuthLayout';
import GlassCard from '../ui/GlassCard';
import Button from '../ui/Button';

/**
 * Landing Page
 * Completely redesigned with "Modern Glassmorphism" system.
 */
const LandingPage = ({ onNavigate }) => {
  return (
    <AuthLayout
      title="🎓 Fluence"
      subtitle="Master Your Studies with AI"
    >
      <div className="grid md:grid-cols-2 gap-8 px-2">
        {/* Student Card */}
        <GlassCard
          hoverEffect={true}
          delay={1}
          className="border-t-4 border-t-green-500"
        >
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
              <GraduationCap className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-3">
              Students
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed">
              Take AI-powered quizzes, track your mastery, and compete on the leaderboard.
            </p>
          </div>

          <div className="space-y-4">
            <Button
              variant="success"
              className="w-full text-lg py-4"
              onClick={() => onNavigate('student-register')}
              icon={BookOpen}
            >
              Join a Class
            </Button>
            <Button
              variant="secondary"
              className="w-full border-green-200 text-green-700 hover:bg-green-50"
              onClick={() => onNavigate('student-login')}
            >
              Student Login
            </Button>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-100 space-y-3">
            <FeatureRow text="Personalized AI Quizzes" color="text-green-600" />
            <FeatureRow text="Real-time Progress Tracking" color="text-green-600" />
            <FeatureRow text="Weekly Leaderboards" color="text-green-600" />
          </div>
        </GlassCard>

        {/* Teacher Card */}
        <GlassCard
          hoverEffect={true}
          delay={2}
          className="border-t-4 border-t-blue-500"
        >
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
              <School className="w-10 h-10 text-blue-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-3">
              Teachers
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed">
              Create classes, generate questions instantly, and track student performance.
            </p>
          </div>

          <div className="space-y-4">
            <Button
              variant="primary"
              className="w-full text-lg py-4"
              onClick={() => onNavigate('teacher-login')}
              icon={User}
            >
              Teacher Login
            </Button>
            <Button
              variant="secondary"
              className="w-full border-blue-200 text-blue-700 hover:bg-blue-50"
              onClick={() => onNavigate('institution-register')}
            >
              New Institution?
            </Button>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-100 space-y-3">
            <FeatureRow text="Automated Question Generation" color="text-blue-600" />
            <FeatureRow text="Detailed Analytics Dashboard" color="text-blue-600" />
            <FeatureRow text="Classroom Management" color="text-blue-600" />
          </div>
        </GlassCard>
      </div>

      <div className="mt-12 text-center text-gray-500 text-sm">
        © 2025 Fluence Education • Built with ❤️ for better learning
      </div>
    </AuthLayout>
  );
};

// Helper component for features
const FeatureRow = ({ text, color }) => (
  <div className="flex items-center gap-2 text-gray-600 font-medium">
    <div className={`p-1 rounded-full bg-gray-100 ${color}`}>
      <ChevronRight className="w-3.5 h-3.5" />
    </div>
    {text}
  </div>
);

export default LandingPage;
